import { z } from 'zod';
import { addOnPricingTypes, pricingTypes } from './types';

const centavosSchema = z.number().int().nonnegative();
const positiveQuantitySchema = z.number().int().positive();
const addOnQuantitySchema = z.number().int().min(1).max(99);
const nonNegativeKgSchema = z
  .number()
  .finite()
  .nonnegative()
  .refine(
    (value: number) => Math.abs(value * 1000 - Math.round(value * 1000)) < 1e-9,
    'KG must have at most 3 decimal places',
  );

export const serviceSchema = z
  .object({
    id: z.string().trim().min(1),
    name: z.string().trim().min(1),
    category: z.string().trim().min(1).optional(),
    description: z.string().optional(),
    pricingType: z.enum(pricingTypes),
    priceCentavos: centavosSchema,
    minimumKg: nonNegativeKgSchema.optional(),
    loadCapacityKg: nonNegativeKgSchema.optional(),
    requiresWeight: z.boolean().optional(),
    requiresItemSelection: z.boolean().optional(),
    allowsQuantity: z.boolean().optional(),
    allowsAddons: z.boolean().optional(),
    active: z.boolean().optional(),
    sortOrder: z.number().int().nonnegative().optional(),
  })
  .superRefine((service, context) => {
    if (service.pricingType === 'PER_LOAD') {
      if (service.priceCentavos <= 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['priceCentavos'],
          message: 'A load service must have a price greater than zero.',
        });
      }
      if (!service.loadCapacityKg || service.loadCapacityKg <= 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['loadCapacityKg'],
          message: 'Set the maximum KG per load.',
        });
      }
    } else if (service.pricingType !== 'PER_ITEM' && service.priceCentavos <= 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['priceCentavos'],
        message: 'Price must be greater than zero.',
      });
    }
  });

export const serviceItemSchema = z.object({
  id: z.string().trim().min(1),
  serviceId: z.string().trim().min(1),
  name: z.string().trim().min(1),
  unitPriceCentavos: centavosSchema.positive(),
  active: z.boolean().optional(),
  sortOrder: z.number().int().nonnegative().optional(),
});

export const addOnSchema = z.object({
  id: z.string().trim().min(1),
  name: z.string().trim().min(1),
  category: z.string().trim().min(1).optional(),
  pricingType: z.enum(addOnPricingTypes),
  priceCentavos: centavosSchema.positive(),
  active: z.boolean().optional(),
  sortOrder: z.number().int().nonnegative().optional(),
});

export const selectedServiceItemSchema = z.object({
  item: serviceItemSchema,
  quantity: positiveQuantitySchema,
});

export const selectedAddOnSchema = z.object({
  addOn: addOnSchema,
  quantity: addOnQuantitySchema.optional(),
});

export const addOnSelectionSnapshotSchema = z.object({
  addonId: z.string().trim().min(1),
  nameSnapshot: z.string().trim().min(1),
  pricingType: z.enum(addOnPricingTypes),
  unitPriceCentavos: centavosSchema,
  quantity: addOnQuantitySchema,
  lineTotalCentavos: centavosSchema,
});

export const discountSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('FIXED'), amountCentavos: centavosSchema }),
  z.object({ type: z.literal('PERCENTAGE'), percentage: z.number().finite().min(0).max(100) }),
]);

export const pricingRequestSchema = z.object({
  service: serviceSchema,
  weightKg: nonNegativeKgSchema.optional(),
  quantity: positiveQuantitySchema.optional(),
  items: z.array(selectedServiceItemSchema).optional(),
  addOns: z.array(selectedAddOnSchema).optional(),
  discount: discountSchema.optional(),
});
